// ---------- General Rules (All States) -------------------------

import { get, getNum, getStr, isEmail, isPhone, isYes } from '@shared';

// R1: Couple status spouse details required
const ruleSpouseDetailsRequired: Rule = {
  id: 'spouse.details.required',
  when: form => isYes(get(form, 'coupleStatus')),
  check: (form): RuleResult => {
    const name = getStr(form, 'spouseName');
    const errs: string[] = [];
    const refs: string[] = [];

    if (!name) {
      errs.push('Spouse/Partner name is required when couple status is Yes.');
      refs.push('spouseName');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'SPOUSE_DETAILS_REQUIRED',
        message: errs.join(' '),
        refs: Array.from(new Set([...refs, 'coupleStatus'])),
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R2: Agent producer code required when indicated
const ruleAgentProducerCodeRequired: Rule = {
  id: 'agent.producer.code.required',
  when: form => isYes(get(form, 'haveAgentProducerCode')),
  check: (form): RuleResult => {
    const code = getStr(form, 'agentProducerCode');

    if (!code) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'AGENT_PRODUCER_CODE_REQUIRED',
        message: 'Agent Producer Code is required when indicated as having one.',
        refs: ['agentProducerCode', 'haveAgentProducerCode'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R3: Account owner details required when different from applicant
const ruleAccountOwnerDetailsRequired: Rule = {
  id: 'account.owner.details.required',
  when: form => {
    const different = get(form, 'accountOwnerDifferent');
    if (Array.isArray(different)) {
      return different.some(d => String(d).toLowerCase() === 'yes');
    }
    return isYes(different);
  },
  check: (form): RuleResult => {
    const name = getStr(form, 'accountOwnerName');
    const email = getStr(form, 'accountOwnerEmail');
    const errs: string[] = [];
    const refs: string[] = [];

    if (!name) {
      errs.push('Account owner name is required.');
      refs.push('accountOwnerName');
    }

    if (!email) {
      errs.push('Account owner email is required.');
      refs.push('accountOwnerEmail');
    } else if (!isEmail(email)) {
      errs.push('Account owner email format looks invalid.');
      refs.push('accountOwnerEmail');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'ACCOUNT_OWNER_DETAILS_REQUIRED',
        message: errs.join(' '),
        refs: Array.from(new Set([...refs, 'accountOwnerDifferent'])),
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R4: Email address format validation
const ruleEmailAddressFormat: Rule = {
  id: 'email.address.format',
  check: (form): RuleResult => {
    const email = getStr(form, 'emailAddress');
    const errs: string[] = [];
    const refs: string[] = [];

    if (email && !isEmail(email)) {
      errs.push('Email address format looks invalid.');
      refs.push('emailAddress');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'EMAIL_FORMAT_INVALID',
        message: errs.join(' '),
        refs,
        category: 'formatting',
      };
    }

    return { ok: true };
  },
};

// R5: Phone number format validation
const rulePhoneNumberFormat: Rule = {
  id: 'phone.number.format',
  check: (form): RuleResult => {
    const phone = getStr(form, 'phoneNumber');
    const providerPhone = getStr(form, 'primaryCareProviderPhone');
    const errs: string[] = [];
    const refs: string[] = [];

    if (phone && !isPhone(phone)) {
      errs.push('Phone number format looks invalid.');
      refs.push('phoneNumber');
    }

    if (providerPhone && !isPhone(providerPhone)) {
      errs.push('Primary care provider phone number format looks invalid.');
      refs.push('primaryCareProviderPhone');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'PHONE_FORMAT_INVALID',
        message: errs.join(' '),
        refs,
        category: 'formatting',
      };
    }

    return { ok: true };
  },
};

// R6: Mailing address required when different address checked
const ruleMailingAddressRequired: Rule = {
  id: 'mailing.address.required',
  when: form => {
    const different = get(form, 'differentAddress');
    if (Array.isArray(different)) {
      return different.some(d => String(d).toLowerCase() === 'yes');
    }
    return isYes(different);
  },
  check: (form): RuleResult => {
    const address = getStr(form, 'mailingAddress');
    const city = getStr(form, 'mailingCity');
    const state = getStr(form, 'mailingState');
    const zip = getStr(form, 'mailingZipCode');
    const errs: string[] = [];
    const refs: string[] = [];

    if (!address) {
      errs.push('Mailing address is required.');
      refs.push('mailingAddress');
    }

    if (!city) {
      errs.push('Mailing city is required.');
      refs.push('mailingCity');
    }

    if (!state) {
      errs.push('Mailing state is required.');
      refs.push('mailingState');
    }

    if (!zip) {
      errs.push('Mailing zip code is required.');
      refs.push('mailingZipCode');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'MAILING_ADDRESS_REQUIRED',
        message: errs.join(' '),
        refs: Array.from(new Set([...refs, 'differentAddress'])),
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R7: Primary care provider details required
const rulePrimaryCareProviderDetailsRequired: Rule = {
  id: 'primary.care.provider.details.required',
  when: form => {
    const name = getStr(form, 'primaryCareProviderName');
    return !!name && !name.toUpperCase().includes('NONE');
  },
  check: (form): RuleResult => {
    const address = getStr(form, 'primaryCareProviderAddress');
    const phone = getStr(form, 'primaryCareProviderPhone');
    const dateSeen = getStr(form, 'primaryCareProviderDateLastSeen');
    const reason = getStr(form, 'primaryCareProviderReasonLastSeen');
    const errs: string[] = [];
    const refs: string[] = [];

    if (!address) {
      errs.push('Primary care provider address is required.');
      refs.push('primaryCareProviderAddress');
    }

    if (!phone) {
      errs.push('Primary care provider phone is required.');
      refs.push('primaryCareProviderPhone');
    }

    if (!dateSeen) {
      errs.push('Date last seen is required.');
      refs.push('primaryCareProviderDateLastSeen');
    }

    if (!reason) {
      errs.push('Reason last seen is required.');
      refs.push('primaryCareProviderReasonLastSeen');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'PRIMARY_CARE_PROVIDER_DETAILS_REQUIRED',
        message: errs.join(' '),
        refs: Array.from(new Set([...refs, 'primaryCareProviderName'])),
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R8: Weight change details required
const ruleWeightChangeDetailsRequired: Rule = {
  id: 'weight.change.details.required',
  when: form => isYes(get(form, 'weightChange')),
  check: (form): RuleResult => {
    const type = getStr(form, 'weightChangeType');
    const pounds = getStr(form, 'weightChangePounds');
    const regimen = getStr(form, 'weightChangeRegimen');
    const errs: string[] = [];
    const refs: string[] = [];

    if (!type) {
      errs.push('Weight change type (lost/gained) is required.');
      refs.push('weightChangeType');
    }

    if (!pounds) {
      errs.push('Number of pounds changed is required.');
      refs.push('weightChangePounds');
    }

    if (!regimen) {
      errs.push('Whether weight change coincided with diet/exercise regimen is required.');
      refs.push('weightChangeRegimen');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'WEIGHT_CHANGE_DETAILS_REQUIRED',
        message: errs.join(' '),
        refs: Array.from(new Set([...refs, 'weightChange'])),
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R9: Height and weight sanity checks
const ruleHeightWeightSanity: Rule = {
  id: 'height.weight.sanity',
  check: (form): RuleResult => {
    const feet = getNum(form, 'heightFeet');
    const inches = getNum(form, 'heightInches');
    const weight = getNum(form, 'weight');
    const errs: string[] = [];
    const refs: string[] = [];

    if (!Number.isNaN(feet) && (feet < 4 || feet > 7)) {
      errs.push('Height (feet) must be between 4 and 7.');
      refs.push('heightFeet');
    }

    if (!Number.isNaN(inches) && (inches < 0 || inches > 11)) {
      errs.push('Height (inches) must be between 0 and 11.');
      refs.push('heightInches');
    }

    if (!Number.isNaN(weight) && (weight < 50 || weight > 500)) {
      errs.push('Weight must be between 50 and 500 pounds.');
      refs.push('weight');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'HEIGHT_WEIGHT_SANITY',
        message: errs.join(' '),
        refs,
        category: 'data-consistency',
      };
    }

    return { ok: true };
  },
};

// R10: Insurability profile eligibility risks
const ruleInsurabilityProfileEligibilityRisk: Rule = {
  id: 'insurability.profile.eligibility.risk',
  check: (form): RuleResult => {
    const issues: string[] = [];
    const refs: string[] = [];

    const disabilityBenefits = get(form, 'socialSecurityDisability');
    if (isYes(disabilityBenefits)) {
      issues.push(
        'Applicant has received Social Security Disability Insurance benefits within past 3 years; this is typically an automatic decline per CareScout guidelines.',
      );
      refs.push('socialSecurityDisability');
    }

    const assistiveDevices = get(form, 'assistiveDevices');
    if (isYes(assistiveDevices)) {
      issues.push(
        'Applicant uses assistive devices (walker, wheelchair, etc.); this is typically an automatic decline per CareScout guidelines.',
      );
      refs.push('assistiveDevices');
    }

    const adlAssistance = get(form, 'adlAssistance');
    if (isYes(adlAssistance)) {
      issues.push(
        'Applicant needs assistance with ADLs; this is typically an automatic decline per CareScout guidelines.',
      );
      refs.push('adlAssistance');
    }

    const homeCareUse = get(form, 'homeCareUse');
    if (isYes(homeCareUse)) {
      issues.push(
        'Applicant currently uses or has been advised to use home care/adult day care; this is typically an automatic decline per CareScout guidelines.',
      );
      refs.push('homeCareUse');
    }

    const ltcFacility = get(form, 'ltcFacility');
    if (isYes(ltcFacility)) {
      issues.push(
        'Applicant resides in, planning to enter, or advised to enter LTC facility; this is typically an automatic decline per CareScout guidelines.',
      );
      refs.push('ltcFacility');
    }

    const seriousConditions = get(form, 'seriousConditions');
    if (isYes(seriousConditions)) {
      issues.push(
        "Applicant answered 'Yes' to serious conditions (Alzheimer's, Dementia, Parkinson's, MS, etc.); this is typically an automatic decline per CareScout guidelines.",
      );
      refs.push('seriousConditions');
    }

    const seriousCancers = get(form, 'seriousCancers');
    if (isYes(seriousCancers)) {
      issues.push(
        'Applicant had treatment for serious cancers (brain, esophagus, liver, pancreas, stomach) in past 4 years; this is typically an automatic decline per CareScout guidelines.',
      );
      refs.push('seriousCancers');
    }

    if (issues.length) {
      return {
        ok: false,
        severity: 'medium-confidence',
        code: 'INSURABILITY_PROFILE_RISK',
        message: issues.join(' '),
        refs,
        category: 'carrier-requirements',
      };
    }

    return { ok: true };
  },
};

// R11: Weight change plausibility
const ruleWeightChangePlausibility: Rule = {
  id: 'weight.change.plausibility',
  when: form => isYes(get(form, 'weightChange')),
  check: (form): RuleResult => {
    const pounds = getNum(form, 'weightChangePounds');

    if (!Number.isNaN(pounds) && pounds < 15) {
      return {
        ok: false,
        severity: 'medium-confidence',
        code: 'WEIGHT_CHANGE_PLAUSIBILITY',
        message:
          'Weight change indicated as Yes but pounds changed is less than 15; verify this is correct.',
        refs: ['weightChange', 'weightChangePounds'],
        category: 'data-consistency',
      };
    }

    return { ok: true };
  },
};

// ---------- STATE-SPECIFIC RULES (AZ) ------------------------------------------

// R12: AZ - AIDS/ARC eligibility risk
const ruleAZAidsEligibilityRisk: Rule = {
  id: 'az.aids.eligibility.risk',
  states: ['AZ'],
  check: (form): RuleResult => {
    const aidsOrArc = get(form, 'aidsOrArc');

    if (isYes(aidsOrArc)) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'AZ_AIDS_ELIGIBILITY_RISK',
        message:
          'Applicant answered "Yes" to AIDS/ARC; this is typically an automatic decline per CareScout guidelines.',
        refs: ['aidsOrArc'],
        category: 'carrier-requirements',
      };
    }

    return { ok: true };
  },
};

// R13: AZ - Advised by medical profession details required
const ruleAZAdvisedByMedicalProfessionDetails: Rule = {
  id: 'az.advised.medical.profession.details',
  states: ['AZ'],
  when: form => isYes(get(form, 'advisedByMedicalProfession')),
  check: (form): RuleResult => {
    const details = getStr(form, 'advisedByMedicalProfessionDetails');

    if (!details) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'AZ_ADVISED_MEDICAL_DETAILS_REQUIRED',
        message:
          'Details are required when expecting medical tests or procedures within next 6 months.',
        refs: ['advisedByMedicalProfessionDetails', 'advisedByMedicalProfession'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// ---------- STATE-SPECIFIC RULES (TX) ------------------------------------------

// R14: TX - Acknowledgement agent name required
const ruleTXAcknowledgementAgentNameRequired: Rule = {
  id: 'tx.acknowledgement.agent.name.required',
  states: ['TX'],
  when: form => {
    const medicaid = isYes(get(form, 'currentlyEnrolledMedicaid'));
    const existingLtc = isYes(get(form, 'existingLtcInsurance'));
    const previousLtc = isYes(get(form, 'previousLtcInsurance'));
    return medicaid || existingLtc || previousLtc;
  },
  check: (form): RuleResult => {
    const agentName = getStr(form, 'ackAgentName');

    if (!agentName) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'TX_ACK_AGENT_NAME_REQUIRED',
        message:
          'Agent name is required in Acknowledgement of Nonduplication when Medicaid or existing/previous LTC insurance is indicated.',
        refs: ['ackAgentName'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R15: TX - Check ABC replacement details required
const ruleTXCheckABCReplacementDetails: Rule = {
  id: 'tx.check.abc.replacement.details',
  states: ['TX'],
  when: form => getStr(form, 'checkABC') === 'check-a',
  check: (form): RuleResult => {
    const policyNumber = getStr(form, 'checkABCRelatedAPolicyNumber');
    const formNumber = getStr(form, 'checkABCRelatedAFormNumber');
    const errs: string[] = [];
    const refs: string[] = [];

    if (!policyNumber) {
      errs.push('Policy number is required when policy replacement is indicated.');
      refs.push('checkABCRelatedAPolicyNumber');
    }

    if (!formNumber) {
      errs.push('Form number is required when policy replacement is indicated.');
      refs.push('checkABCRelatedAFormNumber');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'TX_CHECK_ABC_REPLACEMENT_DETAILS',
        message: errs.join(' '),
        refs: Array.from(new Set([...refs, 'checkABC'])),
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// ---------- STATE-SPECIFIC RULES (SD) ------------------------------------------

// R16: SD - Substantially reasons other specify required
const ruleSDSubstantiallyReasonsOtherSpecify: Rule = {
  id: 'sd.substantially.reasons.other.specify',
  states: ['SD'],
  when: form => {
    const reasons = get(form, 'substantiallyReasons');
    if (Array.isArray(reasons)) {
      return reasons.some(r => String(r).toLowerCase() === 'other');
    }
    return false;
  },
  check: (form): RuleResult => {
    const specify = getStr(form, 'substantiallyReasonsOther');

    if (!specify) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'SD_SUBSTANTIALLY_REASONS_OTHER_REQUIRED',
        message: 'Please specify other reasons when "Other" is selected.',
        refs: ['substantiallyReasonsOther', 'substantiallyReasons'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R17: SD - Advised by medical profession details required
const ruleSDAdvisedByMedicalProfessionDetails: Rule = {
  id: 'sd.advised.medical.profession.details',
  states: ['SD'],
  when: form => isYes(get(form, 'advisedByMedicalProfession')),
  check: (form): RuleResult => {
    const details = getStr(form, 'advisedByMedicalProfessionDetails');

    if (!details) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'SD_ADVISED_MEDICAL_DETAILS_REQUIRED',
        message:
          'Details are required when expecting medical tests or procedures within next 6 months.',
        refs: ['advisedByMedicalProfessionDetails', 'advisedByMedicalProfession'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// ---------- STATE-SPECIFIC RULES (SC) ------------------------------------------

// R18: SC - Duplication of insurance acknowledgment witness required
const ruleSCDuplicationWitnessRequired: Rule = {
  id: 'sc.duplication.witness.required',
  states: ['SC'],
  when: form => {
    const medicaid = isYes(get(form, 'currentlyEnrolledMedicaid'));
    const existingLtc = isYes(get(form, 'existingLtcInsurance'));
    const previousLtc = isYes(get(form, 'previousLtcInsurance'));
    return medicaid || existingLtc || previousLtc;
  },
  check: (form): RuleResult => {
    const witness = getStr(form, 'duplicationOfInsuranceAckWitness');

    if (!witness) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'SC_DUPLICATION_WITNESS_REQUIRED',
        message:
          'Witness name is required for Duplication of Insurance Acknowledgment when Medicaid or existing/previous LTC insurance is indicated.',
        refs: ['duplicationOfInsuranceAckWitness'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R19: SC - Advised by medical profession details required
const ruleSCAdvisedByMedicalProfessionDetails: Rule = {
  id: 'sc.advised.medical.profession.details',
  states: ['SC'],
  when: form => isYes(get(form, 'advisedByMedicalProfession')),
  check: (form): RuleResult => {
    const details = getStr(form, 'advisedByMedicalProfessionDetails');

    if (!details) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'SC_ADVISED_MEDICAL_DETAILS_REQUIRED',
        message:
          'Details are required when expecting medical tests or procedures within next 6 months.',
        refs: ['advisedByMedicalProfessionDetails', 'advisedByMedicalProfession'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// ---------- STATE-SPECIFIC RULES (WI) ------------------------------------------

// R20: WI - Substantially reasons other specify required
const ruleWISubstantiallyReasonsOtherSpecify: Rule = {
  id: 'wi.substantially.reasons.other.specify',
  states: ['WI'],
  when: form => {
    const reasons = get(form, 'substantiallyReasons');
    if (Array.isArray(reasons)) {
      return reasons.some(r => String(r).toLowerCase() === 'other');
    }
    return false;
  },
  check: (form): RuleResult => {
    const specify = getStr(form, 'substantiallyReasonsOther');

    if (!specify) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'WI_SUBSTANTIALLY_REASONS_OTHER_REQUIRED',
        message: 'Please specify other reasons when "Other" is selected.',
        refs: ['substantiallyReasonsOther', 'substantiallyReasons'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// ---------- STATE-SPECIFIC RULES (CT) ------------------------------------------

// R21: CT - Advised by medical profession details required
const ruleCTAdvisedByMedicalProfessionDetails: Rule = {
  id: 'ct.advised.medical.profession.details',
  states: ['CT'],
  when: form => isYes(get(form, 'advisedByMedicalProfession')),
  check: (form): RuleResult => {
    const details = getStr(form, 'advisedByMedicalProfessionDetails');

    if (!details) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'CT_ADVISED_MEDICAL_DETAILS_REQUIRED',
        message:
          'Details are required when expecting medical tests or procedures within next 6 months.',
        refs: ['advisedByMedicalProfessionDetails', 'advisedByMedicalProfession'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// ---------- EXPORT ------------------------------------------------------------

export const careScoutAZStateRules: Rule[] = [
  ruleAZAidsEligibilityRisk,
  ruleAZAdvisedByMedicalProfessionDetails,
];

export const careScoutTXStateRules: Rule[] = [
  ruleTXAcknowledgementAgentNameRequired,
  ruleTXCheckABCReplacementDetails,
];

export const careScoutSDStateRules: Rule[] = [
  ruleSDSubstantiallyReasonsOtherSpecify,
  ruleSDAdvisedByMedicalProfessionDetails,
];

export const careScoutSCStateRules: Rule[] = [
  ruleSCDuplicationWitnessRequired,
  ruleSCAdvisedByMedicalProfessionDetails,
];

export const careScoutWIStateRules: Rule[] = [ruleWISubstantiallyReasonsOtherSpecify];

export const careScoutCTStateRules: Rule[] = [ruleCTAdvisedByMedicalProfessionDetails];

export const careScoutAllStatesRules: Rule[] = [
  ruleSpouseDetailsRequired,
  ruleAgentProducerCodeRequired,
  ruleAccountOwnerDetailsRequired,
  ruleEmailAddressFormat,
  rulePhoneNumberFormat,
  ruleMailingAddressRequired,
  rulePrimaryCareProviderDetailsRequired,
  ruleWeightChangeDetailsRequired,
  ruleHeightWeightSanity,
  ruleInsurabilityProfileEligibilityRisk,
  ruleWeightChangePlausibility,
  ...careScoutAZStateRules,
  ...careScoutTXStateRules,
  ...careScoutSDStateRules,
  ...careScoutSCStateRules,
  ...careScoutWIStateRules,
  ...careScoutCTStateRules,
];

export function evaluateCSNigo(
  form: PolicyFormSubmissionData,
  state?: string,
): RuleResult[] {
  return careScoutAllStatesRules
    .filter(r => !r.states || !state || r.states.includes(state))
    .filter(r => (r.when ? r.when(form) : true))
    .map(r => {
      try {
        return r.check(form);
      } catch (e) {
        return {
          ok: false,
          severity: 'high-confidence' as const,
          code: 'RULE_RUNTIME',
          message: `Rule ${r.id} failed: ${String(e)}`,
          category: 'general' as const,
        };
      }
    })
    .filter(res => !res.ok);
}
